import * as React from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';

import { Dialog, DialogActions, DialogContent, DialogTitle, Loading } from 'components';
import { EZoneProduct } from 'enums';
import { useFetchSubscriptionPreviewMutation, useUpdateSubscriptionMutation } from 'store/apis/billing.api';
import { useAppSelector } from 'store/store.hook';
import { selectCurrentUserInfo } from 'store/slices/auth.slice';

interface ZoneProductChangeDialogProps {
  visible?: boolean;
  setVisible?: (visible: boolean) => void;
  zoneId: number;
  product: EZoneProduct;
  next?: () => void;
}

export const ZoneProductChangeDialog = ({
  visible = false,
  setVisible = () => {},
  zoneId,
  product,
  next = () => {},
}: ZoneProductChangeDialogProps) => {
  const { orgSlug } = useAppSelector(selectCurrentUserInfo);
  const [fetchSubscriptionPreview, { isLoading: loading_subscriptionPreview, data: subscriptionPreview }] =
    useFetchSubscriptionPreviewMutation();
  const [
    updateSubscription,
    { isLoading: loading_subscription, isSuccess: success_subscription, isError: error_subscription },
  ] = useUpdateSubscriptionMutation();

  React.useEffect(() => {
    fetchSubscriptionPreview({
      orgSlug,
      products: {
        [product]: [zoneId],
      },
    });
  }, [zoneId, product]);

  React.useEffect(() => {
    if (!loading_subscription && success_subscription && !error_subscription) {
      next();
      setVisible(false);
    }
  }, [loading_subscription, success_subscription, error_subscription]);

  const onClose = (event: any, reason: string) => {
    if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
      setVisible(false);
      document.body.removeAttribute('style');
    }
  };

  const onSubmit = () => {
    const products: { basic: Array<number>; premium: Array<number> } = {
      basic: [],
      premium: [],
    };
    products[product] = [zoneId];

    updateSubscription({ orgSlug, products });
  };

  return (
    <Dialog onClose={onClose} open={visible} fullScreen={false}>
      <DialogTitle>
        <Typography variant="h6" component="span">
          Confirm Zone Product Change
        </Typography>
      </DialogTitle>

      <DialogContent>
        {loading_subscriptionPreview ? (
          <Loading text="One moment, loading a preview of how this will change your subscription..." />
        ) : (
          <Box>
            <Typography
              variant="subtitle2"
              sx={{
                color: '#999',
              }}
            >
              {subscriptionPreview?.message}
            </Typography>
          </Box>
        )}
      </DialogContent>

      {!loading_subscriptionPreview ? (
        <DialogActions>
          <Button
            onClick={() => onClose(null, 'anyway')}
            sx={{
              paddingX: 2,
              color: '#999',
            }}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={onSubmit}
            disabled={loading_subscription}
            endIcon={loading_subscription && <CircularProgress color="inherit" size={14} />}
            sx={{
              paddingX: 2,
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      ) : (
        ''
      )}
    </Dialog>
  );
};
